<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSettingRow from '@/components/pos-v2/ui/PosUiSettingRow.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import V2ReservationDurationRulesEditor from './V2ReservationDurationRulesEditor.vue';
  import { prefillStaffEmail } from '@/domain/reservations/reservation-setup';
  import type { IReservationSettings } from '@/interfaces/IReservationSettings';
  const props = defineProps<{
    value: IReservationSettings;
    whatsappAvailable: boolean;
    /** Suggested for staff alerts the first time reservations are turned on. */
    defaultStaffEmail?: string | null;
    /** Largest party the saved tables can seat online; 0 when none can. */
    maxBookableParty: number;
  }>();
  const emit = defineEmits<{
    (event: 'input', value: IReservationSettings): void;
    (event: 'go', section: 'tables'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const durations = [60, 75, 90, 120, 150, 180];
  const leads = [0, 30, 60, 120, 240];
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }
  function update<K extends keyof IReservationSettings>(
    key: K,
    value: IReservationSettings[K]
  ) {
    emit('input', { ...props.value, [key]: value });
  }
  function toggleEnabled(on: boolean) {
    emit(
      'input',
      prefillStaffEmail(props.value, on, props.defaultStaffEmail ?? null)
    );
  }
  function selectedNumber(
    key: 'defaultDurationMin' | 'minLeadMin' | 'reminderHoursBefore',
    event: Event
  ) {
    if (event.target instanceof HTMLSelectElement)
      update(key, Number(event.target.value));
  }
  function minimum(value: number) {
    emit('input', {
      ...props.value,
      minPartyOnline: value,
      maxPartyOnline: Math.max(value, props.value.maxPartyOnline),
    });
  }
  function maximum(value: number) {
    emit('input', {
      ...props.value,
      maxPartyOnline: value,
      minPartyOnline: Math.min(value, props.value.minPartyOnline),
    });
  }
  // Guests above what a table seats are told to call instead of booking.
  const partyGapHint = computed(() => {
    if (!props.value.enabled || !props.value.onlineEnabled) return '';
    if (props.maxBookableParty <= 0)
      return i18nT('reservations.settings.partyNoTablesHint');
    if (props.value.maxPartyOnline <= props.maxBookableParty) return '';
    const from = props.maxBookableParty + 1;
    const to = props.value.maxPartyOnline;
    return i18nT('reservations.settings.partyGapHint', {
      max: props.maxBookableParty,
      range:
        from === to
          ? String(from)
          : i18nT('reservations.tables.coverageRange', { from, to }),
    });
  });
  const whatsappHint = computed(() =>
    i18nT(
      props.whatsappAvailable
        ? 'reservations.settings.whatsappHint'
        : 'reservations.settings.whatsappUnavailable'
    )
  );
</script>

<template>
  <div class="general-settings">
    <PosUiSettingsCard
      flush
      :title="$t('reservations.settings.card.reservations')"
      :description="$t('reservations.settings.card.reservationsHint')"
    >
      <PosUiSettingRow
        :label="$t('reservations.settings.enabled')"
        :hint="$t('reservations.settings.enabledHint')"
      >
        <PosUiSwitch
          :value="value.enabled"
          :aria-label="$t('reservations.settings.enabled')"
          @input="toggleEnabled"
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.onlineEnabled')"
        :hint="$t('reservations.settings.onlineEnabledHint')"
      >
        <PosUiSwitch
          :value="value.onlineEnabled"
          :disabled="!value.enabled"
          :aria-label="$t('reservations.settings.onlineEnabled')"
          @input="update('onlineEnabled', $event)"
        />
      </PosUiSettingRow>
    </PosUiSettingsCard>

    <PosUiSettingsCard
      flush
      :title="$t('reservations.settings.card.hours')"
      :description="$t('reservations.settings.card.hoursHint')"
    >
      <PosUiSettingRow
        :label="$t('reservations.settings.defaultDuration')"
        :hint="$t('reservations.settings.defaultDurationHint')"
        input-id="rsv-default-duration"
      >
        <select
          id="rsv-default-duration"
          class="custom-input"
          :value="value.defaultDurationMin"
          @change="selectedNumber('defaultDurationMin', $event)"
        >
          <option v-for="minutes in durations" :key="minutes" :value="minutes">
            {{ $t('reservations.common.minutes', { minutes }) }}
          </option>
          <option
            v-if="!durations.includes(value.defaultDurationMin)"
            :value="value.defaultDurationMin"
          >
            {{
              $t('reservations.common.minutes', {
                minutes: value.defaultDurationMin,
              })
            }}
          </option>
        </select>
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.buffer')"
        :hint="$t('reservations.settings.bufferHint')"
      >
        <PosUiStepper
          :value="value.bufferMin"
          :min="0"
          :max="60"
          :step="5"
          :aria-label="$t('reservations.settings.buffer')"
          @input="update('bufferMin', $event)"
        />
      </PosUiSettingRow>
      <PosUiSettingRow stack :label="$t('reservations.settings.durationRules')">
        <V2ReservationDurationRulesEditor
          :value="value.durationRules"
          @input="update('durationRules', $event)"
        />
      </PosUiSettingRow>
    </PosUiSettingsCard>

    <PosUiSettingsCard
      flush
      :title="$t('reservations.settings.card.rules')"
      :description="$t('reservations.settings.card.rulesHint')"
    >
      <PosUiSettingRow
        wide
        :label="$t('reservations.settings.partyOnline')"
        :hint="$t('reservations.settings.partyMaxHint')"
      >
        <template #note>
          <!-- Always mounted so screen readers announce the note when it appears. -->
          <p class="party-gap" role="status">
            <template v-if="partyGapHint"
              ><PosUiIcon name="alert-outline" :size="16" /><span
                >{{ partyGapHint }}
                <button
                  type="button"
                  class="party-gap-link"
                  @click="emit('go', 'tables')"
                >
                  {{ $t('reservations.settings.viewTables') }}
                </button></span
              ></template
            >
          </p>
        </template>
        <div class="party-range">
          <div class="party-field">
            <span>{{ $t('reservations.settings.partyMin') }}</span>
            <PosUiStepper
              :value="value.minPartyOnline"
              :min="1"
              :max="200"
              :aria-label="$t('reservations.settings.partyMin')"
              @input="minimum"
            />
          </div>
          <div class="party-field">
            <span>{{ $t('reservations.settings.partyMax') }}</span>
            <PosUiStepper
              :value="value.maxPartyOnline"
              :min="1"
              :max="200"
              :aria-label="$t('reservations.settings.partyMax')"
              @input="maximum"
            />
          </div>
        </div>
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.minLead')"
        :hint="$t('reservations.settings.minLeadHint')"
        input-id="rsv-min-lead"
      >
        <select
          id="rsv-min-lead"
          class="custom-input"
          :value="value.minLeadMin"
          @change="selectedNumber('minLeadMin', $event)"
        >
          <option v-for="minutes in leads" :key="minutes" :value="minutes">
            {{ $t('reservations.common.minutes', { minutes }) }}
          </option>
          <option
            v-if="!leads.includes(value.minLeadMin)"
            :value="value.minLeadMin"
          >
            {{
              $t('reservations.common.minutes', { minutes: value.minLeadMin })
            }}
          </option>
        </select>
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.maxAdvance')"
        :hint="
          $t('reservations.settings.daysAhead', { count: value.maxAdvanceDays })
        "
      >
        <PosUiStepper
          :value="value.maxAdvanceDays"
          :min="1"
          :max="365"
          :aria-label="$t('reservations.settings.maxAdvance')"
          @input="update('maxAdvanceDays', $event)"
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.tolerance')"
        :hint="$t('reservations.settings.toleranceHint')"
      >
        <PosUiStepper
          :value="value.toleranceMin"
          :min="0"
          :max="60"
          :step="5"
          :aria-label="$t('reservations.settings.tolerance')"
          @input="update('toleranceMin', $event)"
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.pacing')"
        :hint="$t('reservations.settings.pacingHint')"
        input-id="rsv-pacing"
      >
        <PosUiInput
          id="rsv-pacing"
          type="number"
          inputmode="numeric"
          :min="1"
          :max="32767"
          :step="1"
          clearable
          :value="value.pacingMaxCoversPerSlot"
          :placeholder="$t('reservations.settings.pacingUnlimited')"
          @input="
            update(
              'pacingMaxCoversPerSlot',
              $event === '' ? null : Number($event)
            )
          "
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.autoConfirm')"
        :hint="$t('reservations.settings.autoConfirmHint')"
      >
        <PosUiSwitch
          :value="value.autoConfirm"
          :aria-label="$t('reservations.settings.autoConfirm')"
          @input="update('autoConfirm', $event)"
        />
      </PosUiSettingRow>
    </PosUiSettingsCard>

    <PosUiSettingsCard
      flush
      :title="$t('reservations.settings.card.alerts')"
      :description="$t('reservations.settings.card.alertsHint')"
    >
      <PosUiSettingRow
        :label="$t('reservations.settings.staffEmail')"
        :hint="$t('reservations.settings.staffEmailHint')"
        input-id="rsv-staff-email"
      >
        <PosUiInput
          id="rsv-staff-email"
          type="email"
          :maxlength="255"
          :value="value.staffEmail"
          @input="update('staffEmail', $event || null)"
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.reminders')"
        :hint="$t('reservations.settings.remindersHint')"
      >
        <PosUiSwitch
          :value="value.remindersEnabled"
          :aria-label="$t('reservations.settings.reminders')"
          @input="update('remindersEnabled', $event)"
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        v-if="value.remindersEnabled"
        :label="$t('reservations.settings.secondReminder')"
        :hint="$t('reservations.settings.secondReminderHint')"
        input-id="rsv-second-reminder"
      >
        <select
          id="rsv-second-reminder"
          class="custom-input"
          :value="value.reminderHoursBefore"
          @change="selectedNumber('reminderHoursBefore', $event)"
        >
          <option :value="0">
            {{ $t('reservations.settings.secondReminderOff') }}
          </option>
          <option v-for="hours in [1, 2, 3, 4]" :key="hours" :value="hours">
            {{ $t('reservations.settings.hoursBefore', { hours }) }}
          </option>
        </select>
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.notifyMe')"
        :hint="$t('reservations.settings.notifyMeHint')"
      >
        <PosUiSwitch
          :value="value.notifyMeEnabled"
          :aria-label="$t('reservations.settings.notifyMe')"
          @input="update('notifyMeEnabled', $event)"
        />
      </PosUiSettingRow>
      <PosUiSettingRow
        :label="$t('reservations.settings.whatsapp')"
        :hint="whatsappHint"
      >
        <PosUiSwitch
          :value="whatsappAvailable && value.whatsappEnabled"
          :disabled="!whatsappAvailable"
          :aria-label="$t('reservations.settings.whatsapp')"
          @input="update('whatsappEnabled', $event)"
        />
      </PosUiSettingRow>
    </PosUiSettingsCard>
  </div>
</template>

<style scoped lang="scss">
  .general-settings {
    @apply tw-flex tw-flex-col tw-gap-6;
    .custom-input {
      @apply tw-min-h-11 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-700;
    }
    .party-gap {
      @apply tw-mb-0 tw-mt-2 tw-flex tw-max-w-xl tw-items-start tw-gap-1.5 tw-text-xs tw-leading-relaxed tw-text-amber-800;
      // Empty it is 0px tall but stays in the accessibility tree (live region).
      &:empty {
        @apply tw-mt-0;
      }
      .pos-ui-icon {
        @apply tw-mt-0.5 tw-shrink-0;
      }
    }
    // Inline text link with a touch-sized hit area that adds no visual height.
    .party-gap-link {
      @apply tw-inline tw--mx-1 tw--my-[15px] tw-rounded-sm tw-border-0 tw-bg-transparent tw-px-1 tw-py-[15px] tw-text-xs tw-font-medium tw-text-amber-900 tw-underline hover:tw-text-amber-950 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40 focus-visible:tw-ring-offset-1;
    }
    .party-range {
      @apply tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-3;
      // Not a <label>: a label would forward clicks to the stepper's "−" button.
      .party-field {
        @apply tw-flex tw-flex-col tw-gap-1;
        span {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }
    }
  }
</style>
